import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    inject,
    Input,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { PushPipe } from '@ngrx/component';
import { WdxButtonStyle } from '@wdx/shared/components/wdx-button';
import {
    Country,
    FeatureFlag,
    FeaturesService,
    FieldDefinition,
    FormDefinition,
    FormElementLayoutDefinition,
    MANUAL_ADDRESS_FORM_ID,
    PendingChangeStatusType,
    PostalAddress,
    PostalAddressLookup,
    TRANSLATION_ADDRESS_CONTROL_MANUAL_INPUT,
    TRANSLATION_ADDRESS_CONTROL_SMART_INPUT,
    Trigger,
} from '@wdx/shared/utils';
import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatest,
    tap,
    of,
    noop,
} from 'rxjs';
import {
    debounceTime,
    delay,
    filter,
    map,
    pairwise,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { addressesReducers } from '../../../+state/addresses';
import * as addressesActions from '../../../+state/addresses/addresses.actions';
import * as addressesSelectors from '../../../+state/addresses/addresses.selectors';
import { BaseWdxFormControlClass } from '../../../abstract-classes/base-form-control.class';
import {
    LayoutAndDefinitionEntry,
    ReactiveFormElement,
    ReactiveFormLayoutAndDefinition,
    ValidationSummaryError,
    ValidationSummarySection,
} from '../../../models';
import {
    FormBuilderService,
    FormConditionsService,
    FormControlService,
    FormValidationService,
} from '../../../services';
import { AddressFormatService } from './services/address-format.service';
import {
    ADDRESS_CONTROL_ADDRESS_TYPE,
    AddressTypeService,
} from './services/address-type.service';
import * as R from 'ramda';

@Component({
    selector: 'wdx-ff-address-control',
    templateUrl: './form-address-control.component.html',
    styleUrls: ['./form-address-control.component.scss'],
    host: { class: 'd-flex flex-column' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [FormConditionsService, PushPipe, AddressTypeService],
    standalone: false,
})
export class FormAddressControlComponent
    extends BaseWdxFormControlClass
    implements OnInit, OnChanges
{
    @Input() entityId!: string;
    @ViewChild(NgSelectComponent) addressSelect!: NgSelectComponent;

    public readonly ADDRESS_CONTROL_ADDRESS_TYPE = ADDRESS_CONTROL_ADDRESS_TYPE;
    public readonly TRANSLATION_ADDRESS_CONTROL_MANUAL_INPUT =
        TRANSLATION_ADDRESS_CONTROL_MANUAL_INPUT;
    public readonly TRANSLATION_ADDRESS_CONTROL_SMART_INPUT =
        TRANSLATION_ADDRESS_CONTROL_SMART_INPUT;

    countries$!: Observable<Country[]>;
    addressSearchResults$: Observable<PostalAddressLookup[]> = of([]);
    addressItems$!: Observable<PostalAddressLookup[]>;
    addressSearchResultsIsLoading$!: Observable<boolean>;
    fullAddress$!: Observable<PostalAddress>;
    fullAddressIsLoading$!: Observable<boolean>;
    fullAddressHasError$!: Observable<boolean>;
    identifier$ = new BehaviorSubject<string>(null as any);
    hasValue$ = new BehaviorSubject<boolean>(false);
    smartAddressFields$ = new BehaviorSubject<string[]>(null as any);
    validationSummary$ = new BehaviorSubject<ValidationSummarySection>(
        null as any,
    );
    addressInput$ = new Subject<string>();
    validatorsEnabled$ = new BehaviorSubject<boolean | undefined>(undefined);
    selectedAddressDisplay$ = new BehaviorSubject<PostalAddressLookup | null>(
        null,
    );

    hasAddress = false;
    addressLookupEnabled!: boolean;
    isSmartView = false;
    requiredFields!: string[];
    controlId = Date.now().toString();
    WdxButtonStyle = WdxButtonStyle;
    addressFormDefinition!: FormDefinition;
    index?: number;
    fieldsToNullCheck: string[] = [];
    formGroup!: FormGroup;
    layoutAndDefinitionEntry$!: Observable<readonly LayoutAndDefinitionEntry[]>;
    submitted = false;
    alwaysHideAddressType = false;
    private manualAddressFormTriggers?: Trigger[] | null;
    private formGroupPath?: string;

    public childCondService = inject(FormConditionsService);
    public formBuilderService = inject(FormBuilderService);
    public addressTypeService = inject(AddressTypeService);
    private addressFormatService = inject(AddressFormatService);
    private elementRef = inject(ElementRef);
    private store$ = inject(
        Store<{
            [addressesReducers.FEATURE_KEY]: addressesReducers.State;
        }>,
    );
    private featuresService = inject(FeaturesService);
    private formValidationService = inject(FormValidationService);
    private formControlService = inject(FormControlService);
    private cdr = inject(ChangeDetectorRef);
    public rootCondService = this.formBuilderService.formConditionsService;

    get addressDisableAllFields(): boolean {
        return Boolean(
            this.formElement.pendingChange &&
            !this.formElement.pendingChange?.subItems?.length,
        );
    }

    ngOnInit(): void {
        this.validatorsEnabled$
            .pipe(takeUntil(this.destroyed$), debounceTime(10))
            .subscribe((enabled) => {
                if (R.isNotNil(enabled)) {
                    if (enabled) {
                        this.enableValidators();
                    } else {
                        this.clearValidators();
                    }
                }
            });
        this.configureAddressLookup();
        this.countries$ = this.dynamicDataService.getCountries();
        this.layoutAndDefinitionEntry$ =
            this.childCondService.layoutAndDefinition$.pipe(
                takeUntil(this.destroyed$),
                startWith([]),
                pairwise(),
                filter(
                    ([prev, current]) =>
                        !R.equals(prev, current) && current.length > 0,
                ),
                map(([_, current]) => current),
                take(1),
                map((layoutAndDefinitionEntry) => {
                    layoutAndDefinitionEntry?.[0]?.layoutAndDefinition?.forEach(
                        (field) => {
                            const PENDING_CHANGE =
                                this.formElement?.pendingChange?.subItems?.find(
                                    (subItem) =>
                                        subItem === (field.name as string),
                                );

                            if (
                                this.addressDisableAllFields ||
                                PENDING_CHANGE
                            ) {
                                field.pendingChange = {
                                    status: this.addressDisableAllFields
                                        ? PendingChangeStatusType.Submitted
                                        : this.formElement?.pendingChange
                                              ?.status,
                                };
                            }
                        },
                    );
                    return layoutAndDefinitionEntry;
                }),
                shareReplay(1),
            );

        this.layoutAndDefinitionEntry$
            .pipe(
                takeUntil(this.destroyed$),
                tap((LayoutAndDefinitionEntry) => {
                    this.applyLayout(
                        LayoutAndDefinitionEntry?.[0]
                            .layoutAndDefinition as ReactiveFormLayoutAndDefinition[],
                    );
                }),
                delay(0),
                switchMap(() => this.rootCondService.layoutAndDefinition$),
                take(1),
            )
            .subscribe(() => {
                this.triggerUpdateValidators(this.formControl?.value, true);
                const newIndex =
                    this.formControlService.stringToFormControlObject(
                        this.formGroupPath as string,
                    )?.Level1SubFormIndex;

                // apply triggers from manual address form to the address fields
                this.formBuilderService.applyTriggersToChildControls(
                    this.manualAddressFormTriggers,
                    this.formGroupPath as string,
                    this.formGroup,
                    newIndex,
                );

                // apply parent form conditions to this address
                // NOTE: applying parent form conditions to an address within a subform is not currently implemented!
                this.rootCondService.setupAddressTriggers(
                    this.formElement?.name as string,
                    this.formGroup,
                );
            });

        this.formControl?.valueChanges
            .pipe(
                withLatestFrom(this.formStaticService.submitAttempted$),
                startWith([this.formControl?.value, false]),
                takeUntil(this.destroyed$),
                debounceTime(5),
                tap(([value]) => {
                    if (this.addressLookupEnabled) {
                        this.addressFormatService
                            .getAddress$(this.formControl?.getRawValue(), true)
                            .pipe(takeUntil(this.destroyed$))
                            .subscribe((value) => {
                                this.smartAddressFields$.next(value);
                                this.selectedAddressDisplay$.next(
                                    this.buildSelectedAddress(value),
                                );
                            });
                    }
                    this.triggerUpdateValidators(value);
                }),
                delay(0),
            )
            .subscribe(([_, submitted]) => {
                this.cdr.markForCheck();
                if (submitted) {
                    this.submitted = submitted;
                    this.calculateValidationErrors();
                }
            });

        this.createFormConfigByMergingParentFormConfigWithAddressFormConfig();

        this.addressItems$ = combineLatest([
            this.selectedAddressDisplay$,
            this.addressSearchResults$,
        ]).pipe(
            map(([selected, results]) => [
                ...(selected ? [selected] : []),
                ...(results || []),
            ]),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['formElement'].currentValue) {
            const formElement = changes['formElement']
                .currentValue as ReactiveFormLayoutAndDefinition;
            this.index = formElement.index;
            if (changes['formElement'].firstChange) {
                return;
            }
            if (formElement.isRequired) {
                this.validatorsEnabled$.next(true);
            } else {
                const hasValue = this.rootCondService.formHasAnyValues(
                    this.formControl?.value,
                );
                this.validatorsEnabled$.next(hasValue);
            }
        }
    }

    setUpManualAddress(definition: FormDefinition) {
        this.addFormControls(definition);
        this.cdr.markForCheck();
        this.formStaticService.submitAttempted$.pipe(take(1)).subscribe(() => {
            this.formControl?.markAsTouched();
            this.formControl?.updateValueAndValidity();
            Object.keys((this.formControl as FormGroup).controls).forEach(
                (controlId) => {
                    const control = this.formControl?.get(controlId);
                    control?.markAsTouched();
                    this.cdr.markForCheck();
                },
            );
            this.calculateValidationErrors();
        });
    }

    calculateValidationErrors() {
        this.validationSummary$.next({
            errors: this.formValidationService.getDefinitionErrors(
                this.formElement.children as ReactiveFormLayoutAndDefinition[],
                (this.formControl as any).controls,
            ) as any,
        });
    }

    clearValidators() {
        this.rootCondService.disableFormGroupChildValidators(
            this.formControl as FormGroup,
        );
    }

    enableValidators() {
        this.rootCondService.enableFormGroupChildValidators(
            this.formControl as FormGroup,
        );
        this.calculateValidationErrors();
    }

    convertValueIn(value: Record<string, any>) {
        Object.keys(value).forEach((key) => {
            if (this.formControl?.get(key)) {
                this.formControl.get(key)?.setValue(value[key]);
            }
        });
        this.hasAddress = true;
    }

    configureAddressLookup() {
        this.addressLookupEnabled = this.featuresService.hasFeature(
            FeatureFlag.AddressLookupEnabled,
        );

        if (this.addressLookupEnabled) {
            this.isSmartView = true;
            this.loadAddressLookup();
            this.loadAddressSelectors();
        }
    }

    loadAddressSelectors() {
        this.addressSearchResults$ = this.store$
            .select(addressesSelectors.getAddresses, {
                id: this.controlId,
            })
            .pipe(map((addresses) => addresses || []));

        this.addressSearchResultsIsLoading$ = this.store$
            .select(addressesSelectors.getAddressesIsLoadingList, {
                id: this.controlId,
            })
            .pipe(map(Boolean));

        this.fullAddressIsLoading$ = this.store$
            .select(addressesSelectors.getAddressIsLoadingSingle, {
                id: this.controlId,
            })
            .pipe(map(Boolean));

        this.fullAddressHasError$ = this.store$
            .select(addressesSelectors.getAddressHasLoadingSingleError, {
                id: this.controlId,
            })
            .pipe(map(Boolean));

        this.fullAddress$ = this.store$.select(addressesSelectors.getAddress, {
            id: this.controlId,
        }) as Observable<PostalAddress>;
    }

    loadAddressLookup() {
        this.addressInput$
            .pipe(
                takeUntil(this.destroyed$),
                debounceTime(300),
                filter((searchText) => Boolean(searchText)),
            )
            .subscribe((searchText) => {
                this.store$.dispatch(
                    addressesActions.getAddresses({
                        searchText: searchText,
                        fieldId: this.controlId,
                    }),
                );
            });
    }

    reset() {
        Object.keys(this.formControl?.getRawValue()).forEach((key) => {
            this.formControl?.get(key)?.setValue(null);
        });
        this.hasAddress = false;
        this.selectedAddressDisplay$.next(null);
    }

    trackByFn(address: PostalAddressLookup): string {
        return address.identifier as string;
    }

    onLookupSearch(event: { term: string }): void {
        const { term } = event;

        if (!term) {
            return;
        }

        this.store$.dispatch(
            addressesActions.getAddresses({
                searchText: term,
                fieldId: this.controlId,
            }),
        );
    }

    onLookupClear(): void {
        this.addressSelect.clearModel();
        this.selectedAddressDisplay$.next(null);
        this.reset();
    }

    onLookupSelected(address: PostalAddressLookup): void {
        if (!address) {
            this.reset();
            return;
        }

        const { identifier } = address;

        this.store$.dispatch(
            addressesActions.getAddress({
                addressId: identifier as string,
                fieldId: this.controlId,
            }),
        );

        this.fullAddress$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((address) => {
                if (!address) {
                    return;
                }
                this.convertValueIn({
                    ...address,
                });
            });
    }

    private buildSelectedAddress(
        value: string[] | null,
    ): PostalAddressLookup | null {
        if (!Array.isArray(value)) {
            return null;
        }
        const displayText = value
            .filter((part) => part && part.trim())
            .join(', ');
        return displayText
            ? {
                  identifier: displayText,
                  displayText,
              }
            : null;
    }

    /**
     * This method creates the form controls and figures out what (if any) data to use
     *
     * Full Details:
     * The FF Address Control (this component) is different from all other FF controls - it adds it's own form controls using the
     * ManualAddressForm formDef. Also remember this control can be used either directly embedded in a form or as part of an array/subform.
     * When used in an array we need to handle the scenario where the API formData contains data for an array of addresses, if one of these
     * addresses is deleted from the parent form and the user creates a new address entry (re-using the same index) we need to detect this and
     * create an empty address instance.
     *
     * @param formDefinition ManualAddressForm formDefinition
     */
    addFormControls(formDefinition: FormDefinition) {
        const formData = this.formStaticService.formData?.data;
        const arrayMode = typeof this.formElement.index === 'number';

        let data =
            this.formElement.parentName &&
            formData?.[this.formElement.parentName]
                ? formData[this.formElement.parentName][
                      this.formElement.index as number
                  ]
                : formData;

        // create/check initDone flag on data, this is so we can detect if we've already initialised a component instance with this data
        if (arrayMode && data) {
            if (data?.initDone) {
                data = undefined;
            } else {
                data.initDone = true;
            }
        }

        formDefinition?.layout?.sectionLayoutDefinitions?.[0]?.elementLayoutDefinitions?.forEach(
            (layout) => {
                const fieldDef = formDefinition.schema?.find(
                    ({ name }) => name === layout.name,
                );
                if (!fieldDef) {
                    return;
                }
                const control = new FormControl(
                    data && data[this.formElement.name as string]
                        ? data[this.formElement.name as string][
                              layout.name as string
                          ]
                        : null,
                    this.formValidationService.getValidators(
                        fieldDef as FieldDefinition,
                        layout,
                    ),
                );

                (this.formControl as FormGroup).addControl(
                    layout.name as string,
                    control,
                );
            },
        );
    }

    applyLayout(layoutAndDefinition: ReactiveFormLayoutAndDefinition[]): void {
        this.rootCondService.updateControlChildren(
            this.formElement,
            this.formControl as AbstractControl,
            layoutAndDefinition as ReactiveFormElement[],
        );
    }

    onErrorClicked(error: ValidationSummaryError): void {
        const fieldDefinitionElementRef =
            this.elementRef.nativeElement.querySelector(
                `[data-form-control='${error.name}']`,
            );
        this.formValidationService.scrollToError(fieldDefinitionElementRef);
    }

    onToggleView() {
        this.isSmartView = !this.isSmartView;
    }

    private createFormConfigByMergingParentFormConfigWithAddressFormConfig() {
        this.dynamicDataService
            .getFormLayoutAndDefinition(MANUAL_ADDRESS_FORM_ID)
            .pipe(take(1))
            .subscribe(({ definition }) => {
                const { path } =
                    this.formControlService.getMetadataFromFormControl(
                        this.formControl as AbstractControl,
                    );
                this.formGroupPath = path as string;
                const pathWithoutSubformIndex = this.formGroupPath?.replaceAll(
                    /\[\d*]/g,
                    '',
                );
                const prefix = pathWithoutSubformIndex ?? this.formElement.name;

                let updatedAddressFormTriggers: Trigger[] | undefined =
                    undefined;

                // we need to update all target address fields with the parent field name as a prefix,
                // there are lots of possible places these could be so it's more reliable to edit as a JSON string rather
                // than try and and drilldown into all possible places as likely some would be missed
                try {
                    updatedAddressFormTriggers = JSON.parse(
                        JSON.stringify(
                            definition.conditions?.triggers,
                        ).replaceAll('"name":"', `"name":"${prefix}.`),
                    );
                    updatedAddressFormTriggers =
                        updatedAddressFormTriggers?.map((trigger) => ({
                            ...trigger,
                            field: `${prefix}.${trigger?.field}`,
                        }));
                } catch {
                    noop();
                }
                this.manualAddressFormTriggers = updatedAddressFormTriggers;

                const updatedDefinition =
                    this.addressTypeService.overrideDefinition(
                        structuredClone(definition),
                        this.formElement,
                    );
                const parentFieldSchemas: Record<string, FieldDefinition> = {};

                this.formGroup = (
                    this.controlContainer.control as FormGroup
                ).get(this.formElement.name as string) as FormGroup;

                if (
                    updatedDefinition?.layout?.sectionLayoutDefinitions?.[0]
                        .elementLayoutDefinitions
                ) {
                    updatedDefinition.layout.sectionLayoutDefinitions[0].elementLayoutDefinitions =
                        updatedDefinition.layout.sectionLayoutDefinitions[0].elementLayoutDefinitions.map(
                            (innerElement) => {
                                const parentSchemaItem =
                                    this.formStaticService.getFieldDefinitionForFieldFromFormDef(
                                        `${path}.${innerElement.name}`,
                                    );
                                let parentSchemaConfigThatOverridesLayout: FormElementLayoutDefinition =
                                    {
                                        elementType: innerElement.elementType,
                                        name: innerElement.name,
                                    };

                                if (parentSchemaItem) {
                                    if (
                                        parentSchemaItem.name !==
                                        innerElement.name
                                    ) {
                                        console.error(
                                            `address form schema mapping to parent form is incorrect for field: ${parentSchemaItem.name}`,
                                        );
                                    } else {
                                        parentFieldSchemas[innerElement.name] =
                                            parentSchemaItem as FieldDefinition;
                                        // we need to use the parent schema to override the child layout for any overlapping values e.g. isRequired
                                        // we need to do this as normally layout will override schema, but here the parent form should always win
                                        const parentSchemaAsLayout =
                                            new FormElementLayoutDefinition({
                                                ...parentSchemaConfigThatOverridesLayout,
                                                ...parentSchemaItem,
                                            } as any);
                                        parentSchemaConfigThatOverridesLayout =
                                            {
                                                ...parentSchemaConfigThatOverridesLayout,
                                                ...R.filter(
                                                    R.isNotNil,
                                                    parentSchemaAsLayout as any,
                                                ),
                                            };
                                    }
                                }
                                const parentFormFieldLayout =
                                    this.formStaticService.getLayoutForFieldFromFormDef(
                                        `${path}.${innerElement.name}`,
                                    );
                                if (
                                    parentFormFieldLayout &&
                                    parentFormFieldLayout.name !==
                                        innerElement.name
                                ) {
                                    console.error(
                                        `address form layout mapping to parent form is incorrect for field: ${innerElement.name}`,
                                    );
                                    return innerElement;
                                }
                                let addressTypeOptionsOverride = {};
                                if (
                                    innerElement.name ===
                                    ADDRESS_CONTROL_ADDRESS_TYPE
                                ) {
                                    addressTypeOptionsOverride = {
                                        options:
                                            this.addressTypeService
                                                .addressTypeSchema.options,
                                    };
                                }
                                return {
                                    ...innerElement,
                                    ...(parentFormFieldLayout ?? {}),
                                    ...parentSchemaConfigThatOverridesLayout,
                                    ...addressTypeOptionsOverride,
                                } as FormElementLayoutDefinition;
                            },
                        );
                }

                updatedDefinition.schema = updatedDefinition.schema?.map(
                    (item) => {
                        return {
                            ...item,
                            ...(parentFieldSchemas[item?.name as string] ?? []),
                        };
                    },
                );

                this.setUpManualAddress(updatedDefinition);

                this.childCondService.configService(
                    this.formGroup,
                    updatedDefinition,
                    this.formStaticService.formData,
                    updatedDefinition.name as string,
                    this.fieldsToNullCheck,
                    this.formStaticService.initialisationMode,
                    { isChildInstance: true },
                );
            });
    }

    private triggerUpdateValidators(formValue: any, force = false) {
        const hasValue = this.rootCondService.formHasAnyValues(formValue);
        const shouldHaveValidators = this.formElement.isRequired || hasValue;
        if (force || this.validatorsEnabled$.value !== shouldHaveValidators) {
            this.validatorsEnabled$.next(shouldHaveValidators);
        }
    }
}
